import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { EnvironmentLoaderServiceConfig } from '@rci/environment-loader';
import { fidoConfigProvider, FidoConfigurations } from '@rci/self-serve-fido/app.config';
import { AnalyticsStateService } from '@rci/self-serve-fido/core/analytics/analytics-state.service';
import { analyticsServiceFactory } from '@rci/self-serve-fido/core/analytics/analytics.service.factory';
import { CoreModule } from '@rci/self-serve-fido/core/core.module';
import { FidoLoginStateService } from '@rci/self-serve-fido/core/fido-login-state/fido-login-state.service';
import { ResponseInterceptorService } from '@rci/self-serve-fido/core/http/response-interceptor.service';
import { RecaptchaKeysConfig } from '@rci/self-serve-fido/services/recaptcha/recaptcha-keys-config.service';
import {
  ApiEndpointConfigService,
  getCommonProviders,
  GlobalAppConfigService,
  SemafoneConfigService,
} from '@rci/self-serve-shared';
import { CDKCoreModule } from '@rogers/cdk/core';
import { LoaderComponent, LoaderService } from '@rogers/cdk/loader';
import { LoaderModule } from '@rogers/self-serve-utils/loader';
import { ResponsiveModule } from '@rogers/ngx-responsive';
import { oidcInterceptor, OidcInterceptor } from '@rogers/self-serve-oidc-interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { RESPONSIVE_CONFIG } from './app.constant';
import { provideAnalyticsConfiguration, provideSelfServeAnalyticsService } from './core/analytics';
import { secondaryNavAnalyticsConfiguration } from './core/secondary-nav/secondary-nav-analytics-config';
import { environmentConfigurator } from './environment-configurator/environment-configurator.function';
import { SharedModule } from './shared/shared.module';
import { AppStoreModule } from './state/app-store.module';
import { PdoConfigService } from '@rogers/self-serve-my-offers/pdo-config';
import { CustomTileModule } from '@rogers/self-serve-utils/custom-tile';
import { APP_BASE_HREF } from '@angular/common';

declare const __BUILD_TIME_STAMP__: string;
declare const __APP_BASE_HREF__: string;

// eslint-disable-next-line
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', `.json?${__BUILD_TIME_STAMP__}`);
}

export function environmentLoaderServiceConfigFactory(): EnvironmentLoaderServiceConfig {
  return {
    ...new EnvironmentLoaderServiceConfig(),
    additionalConfigTypes: [ApiEndpointConfigService, RecaptchaKeysConfig],
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    AppStoreModule,
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    HttpClientModule,
    ResponsiveModule.forRoot(RESPONSIVE_CONFIG),
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    LoaderComponent,
    LoaderModule,
    CDKCoreModule.forRoot({
      brand: 'fido',
    }),
    CustomTileModule.forRoot({ leftBorderColor: 'teal' }),
  ],
  providers: [
    {
      provide: APP_BASE_HREF,
      useValue: `${__APP_BASE_HREF__}`,
    },
    ...getCommonProviders(
      analyticsServiceFactory,
      FidoLoginStateService,
      ResponseInterceptorService,
      AnalyticsStateService,
      GlobalAppConfigService,
      PdoConfigService,
      environmentConfigurator,
      SemafoneConfigService,
      environmentLoaderServiceConfigFactory,
    ),
    { provide: FidoConfigurations, useValue: fidoConfigProvider },
    provideAnalyticsConfiguration(secondaryNavAnalyticsConfiguration),
    provideSelfServeAnalyticsService(),
    LoaderService,
    OidcInterceptor.configure(),
    oidcInterceptor, // this should be the last entry always
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
